import React, { useEffect, useMemo, useState } from "react";
import cls from "./AdminMembersStats.module.scss";
import api from "@/utils/axios";
import { Link } from "react-router-dom";

const Icon = ({ name }) => {
  // Simple inline icons (no extra libs)
  switch (name) {
    case "pending":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 5v6l4 2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "creating":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M4 20h4l10-10-4-4L4 16v4Zm9-13 4 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "approved":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M20 6 9 17l-5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "profileMissing":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2m9.5-11a4 4 0 1 1-6 0 4 4 0 0 1 6 0ZM18 8h4m-2-2v4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "email":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M4 6h16v12H4V6Zm0 2 8 6 8-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "suspended":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-5 10h10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "banned":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-6.2 3.8 14.4 14.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "deleted":
      return (
        <svg viewBox="0 0 24 24" className={cls.ico} aria-hidden="true">
          <path
            d="M4 7h16M9 7V5h6v2m-8 0 1 14h8l1-14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
};

const AdminMembersStats = () => {
  const [stats, setStats] = useState({
    membersJustRegisteredNotEmailVerified: 0,
    membersStillCreatingProfile: 0,
    membersThatPending: 0,
    membersThatApproved: 0,
    membersWithoutProfile: 0,
    suspendedUsers: 0,
    bannedUsers: 0,
    deletedUsers: 0,
    totalActiveMembersWithProfile: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/v1/admin/members/stats");
        setStats(res.data);
      } catch (err) {
        console.error("Failed to fetch member stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const cards = useMemo(
    () => [
      {
        key: "membersThatPending",
        to: "/admin/members/that-pending",
        label: "Waiting For Approve",
        icon: "pending",
        className: cls.membersThatPending,
        value: stats.membersThatPending,
      },
      {
        key: "membersStillCreatingProfile",
        to: "/admin/members/still-creating-profile",
        label: "Still Creating Profile",
        icon: "creating",
        className: cls.membersStillCreatingProfile,
        value: stats.membersStillCreatingProfile,
      },
      {
        key: "membersThatApproved",
        to: "/admin/members/that-approved",
        label: "Approved Members",
        icon: "approved",
        className: cls.membersThatApproved,
        value: stats.membersThatApproved,
      },
      {
        key: "membersWithoutProfile",
        to: "/admin/members/without-profile",
        label: "Members without Profile",
        icon: "profileMissing",
        className: cls.membersWithoutProfile,
        value: stats.membersWithoutProfile,
      },
      {
        key: "membersJustRegisteredNotEmailVerified",
        to: "/admin/members/just-registered-not-email-verified",
        label: "Email Unverified",
        icon: "email",
        className: cls.membersJustRegisteredNotEmailVerified,
        value: stats.membersJustRegisteredNotEmailVerified,
      },
      {
        key: "suspendedUsers",
        to: "/admin/members/suspended-users",
        label: "Suspended Users",
        icon: "suspended",
        className: cls.suspendedUsers,
        value: stats.suspendedUsers,
      },
      {
        key: "bannedUsers",
        to: "/admin/members/banned-users",
        label: "Banned Users",
        icon: "banned",
        className: cls.bannedUsers,
        value: stats.bannedUsers,
      },
      {
        key: "deletedUsers",
        to: "/admin/members/deleted-users",
        label: "Deleted Users",
        icon: "deleted",
        className: cls.deletedUsers,
        value: stats.deletedUsers,
      },
    ],
    [stats],
  );

  if (loading) {
    return (
      <div className={cls.page}>
        <div className={cls.shell}>
          <div className={cls.card}>
            <div className={cls.header}>
              <div className={cls.kicker} />
              <div className={cls.titleSkeleton} />
              <div className={cls.subSkeleton} />
            </div>

            <div className={cls.grid}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className={cls.skelTile} />
              ))}
            </div>

            <div className={cls.footerRow}>
              <div className={cls.totalSkeleton} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.bgOrbs} aria-hidden="true">
        <span className={cls.orbA} />
        <span className={cls.orbB} />
        <span className={cls.orbC} />
      </div>

      <div className={cls.shell}>
        <section className={cls.card}>
          <header className={cls.header}>
            <div className={cls.kicker}>Admin</div>
            <h2 className={cls.title}>Member Statistics</h2>
            <p className={cls.subtitle}>
              Quick snapshot of member lifecycle and moderation states.
            </p>

            <div className={cls.totalPill}>
              <span className={cls.totalLabel}>Total Members</span>
              <span className={cls.totalValue}>
                {stats.totalActiveMembersWithProfile}
              </span>
            </div>
          </header>

          <div className={cls.grid}>
            {cards.map((c) => (
              <Link
                key={c.key}
                to={c.to}
                className={`${cls.tile} ${c.className}`}
                aria-label={`${c.label}: ${c.value}`}
              >
                <div className={cls.tileTop}>
                  <div className={cls.iconWrap}>
                    <Icon name={c.icon} />
                  </div>
                  <div className={cls.count} title={String(c.value)}>
                    {c.value}
                  </div>
                </div>

                <div className={cls.labelRow}>
                  <div className={cls.label}>{c.label}</div>
                  <span className={cls.ctaHint}>View</span>
                </div>

                <div className={cls.tileGlow} aria-hidden="true" />
              </Link>
            ))}
          </div>

          <div className={cls.footerRow}>
            <div className={cls.note}>
              Tip: click a card to open the filtered members list.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminMembersStats;
